import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../store/auth'
import { login as apiLogin } from '../api/auth'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { AuthLayout, AuthError } from '../components/layout/AuthLayout'

// Bypasses /choose-app only when the user has exactly one role total — a user with two or more
// roles (even if one of them has a dedicated landing path below) still goes to /choose-app,
// since guessing priority between two real roles isn't this map's job.
const SINGLE_ROLE_LANDING: Record<string, string> = {
  'Open-seat': '/open',
  'energy-os-user': '/energy-os/dashboard',
  'garage-user': '/garage',
}

function landingPathFor(roles: string[]): string {
  if (roles.length === 1 && SINGLE_ROLE_LANDING[roles[0]]) return SINGLE_ROLE_LANDING[roles[0]]
  if (roles.length > 1) return '/choose-app'
  return '/'
}

export function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [error,    setError]    = useState('')
  const [loading,  setLoading]  = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const { data } = await apiLogin(email, password)
      login(data.accessToken, data.refreshToken, data.user)
      navigate(landingPathFor(data.user.roles))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg ?? 'Login failed. Check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      title="Sign In"
      subtitle="Enter your email and password to sign in!"
      footer={<>Don&apos;t have an account?{' '}<Link to="/register" className="text-brand-500 hover:text-brand-600">Register your business</Link></>}
    >
      {error && <AuthError>{error}</AuthError>}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Field label="Email">
          <input type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="info@gmail.com" className="field" />
        </Field>
        <Field label="Password">
          <input type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password" className="field" />
        </Field>
        <Button type="submit" size="sm" disabled={loading} loading={loading} className="w-full">
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  )
}
